import React from "react";
import {
  TbBuildingStore,
  TbLayoutDashboard,
  TbCalendarEvent,
  TbBrandWhatsapp,
  TbBrandInstagram,
  TbUsers,
  TbList,
  TbBriefcase,
  TbMessage,
  TbCalendar,
  TbLock,
  TbHourglass,
  TbSpeakerphone,
  TbUserCog,
  TbPlus,
  TbClock,
  TbFilter,
  TbGift,
  TbRepeat,
  TbChartBar,
  TbHeadset,
  TbSettings,
  TbUsersGroup,
  TbMessageQuestion,
} from "react-icons/tb";

// Ícones Tabler padronizados (sem emoji, sem PNG). Tamanho 18, decorativos.
const ic = (Comp) => <Comp size={18} aria-hidden="true" />;

// Estrutura do menu lateral (design system Nexus).
// Itens `{ type: "section" }` são rótulos de grupo NÃO clicáveis.
// Cada item de ação preserva sua `key` para que os handlers/gating de
// Agenda.js e VisaoGeral.js (via buildMenu) continuem casando.
export const exampleMenu = [
  // ── Operação ───────────────────────────────────────────────────────────
  { type: "section", key: "section-operacao", label: "Operação" },
  {
    // Card "Minha empresa" no topo. label/onClick injetados pelo buildMenu
    // (label = nome real do tenant; onClick → /business/settings).
    key: "empresa",
    label: "Minha empresa",
    icon: ic(TbBuildingStore),
  },
  {
    // Atalho de "Perguntas frequentes" → /business/settings, onde vive o
    // FaqCampaignEditor. `requiresNoScheduling` é o INVERSO dos demais gates:
    // o item só aparece para quem NÃO agenda, porque nesse tier o FAQ é o
    // onboarding principal (ADR-0026 §5) e precisa de um caminho de um clique.
    // Quem tem agenda continua chegando pelo card "Minha empresa", como hoje.
    key: "faq",
    label: "Perguntas frequentes",
    icon: ic(TbMessageQuestion),
    link: "/business/settings",
    requiresNoScheduling: true,
  },
  {
    // Dashboard pós-login. Item com `link` → navega normal nas duas telas.
    key: "visao-geral",
    label: "Visão geral",
    icon: ic(TbLayoutDashboard),
    link: "/visao-geral",
  },
  {
    // Navegação direta para a tela da Agenda (separado do grupo key:"agenda").
    // `requiresScheduling`: no Plano Atendimento o item CONTINUA VISÍVEL, com
    // cadeado (ADR-0026 — bloquear, não esconder). Quem clica cai na tela de
    // upsell, que é o motor de upgrade do tier.
    key: "agenda-home",
    label: "Agenda",
    icon: ic(TbCalendarEvent),
    link: "/agenda",
    requiresScheduling: true,
  },
  {
    // Centro de mensagens → onClick /business/messages-center (no buildMenu).
    key: "messages-dock",
    label: "Conversas",
    icon: ic(TbBrandWhatsapp),
  },
  {
    key: "customers",
    label: "Clientes",
    icon: ic(TbUsers),
    children: [
      {
        // key própria ("customers-list") p/ não colidir com a "list" de
        // Profissionais — colisão marcava aria-current em ambos os itens.
        key: "customers-list",
        label: "Listagem",
        icon: ic(TbList),
        link: "/business/customers",
      },
    ],
  },
  {
    key: "whatsApp",
    label: "Conectar WhatsApp",
    icon: ic(TbBrandWhatsapp),
  },
  {
    // ⚠️ Ação secundária (Coexistence, Fase 1): conecta um número que já roda
    // no app do WhatsApp Business sem desconectá-lo do celular.
    // onClick injetado pelo buildMenu (handleConnectWhatsAppCoexistence).
    key: "whatsAppCoexistence",
    label: "Conectar conta existente do WhatsApp Business",
    icon: ic(TbBrandWhatsapp),
  },
  {
    key: "instagram",
    label: "Conectar Instagram",
    icon: ic(TbBrandInstagram),
  },
  {
    // onClick → /business/services (no buildMenu).
    key: "services",
    label: "Meus serviços",
    icon: ic(TbBriefcase),
  },
  {
    key: "templates",
    label: "Modelos de mensagem",
    icon: ic(TbMessage),
    link: "/business/templates",
  },
  {
    key: "agenda",
    label: "Agenda",
    icon: ic(TbCalendar),
    requiresScheduling: true,
    children: [
      {
        key: "blocks",
        label: "Bloqueios",
        icon: ic(TbLock),
        link: "/business/blocks",
        requiresScheduling: true,
      },
      {
        key: "waitlist",
        label: "Lista de espera",
        icon: ic(TbHourglass),
        link: "/business/waitlist",
        requiresScheduling: true,
      },
      {
        key: "reschedule-broadcast",
        label: "Reagendamento em massa",
        icon: ic(TbSpeakerphone),
        link: "/business/reschedule-broadcast",
        requiresScheduling: true,
      },
    ],
  },
  {
    key: "professionals",
    label: "Profissionais",
    icon: ic(TbUserCog),
    requiresScheduling: true,
    children: [
      {
        key: "list",
        label: "Listagem",
        icon: ic(TbList),
        link: "/business/professionals",
        requiresScheduling: true,
      },
      {
        key: "new_professional",
        label: "Novo profissional",
        icon: ic(TbPlus),
        link: "/business/professionals/new",
        requiresScheduling: true,
      },
      {
        key: "business_hours",
        label: "Expedientes",
        icon: ic(TbClock),
        link: "/business/professionals/hours",
        requiresScheduling: true,
      },
    ],
  },

  {
    // Gestão de setores da equipe. Visível apenas a OWNER/ADMIN (filtro por
    // `requiresRole` no buildMenu); a rota também é protegida por RoleRoute.
    // `requiresCrm`: Setores é 100% Central de Atendimento (CRM), então só
    // aparece no plano Profissional (`crmIncluded`). O inbox base (Conversas)
    // continua visível ao Básico — Setores, não.
    key: "departments",
    label: "Setores",
    icon: ic(TbUsersGroup),
    link: "/business/settings/departments",
    requiresRole: ["OWNER", "ADMIN"],
    requiresCrm: true,
  },

  // ── Crescimento ────────────────────────────────────────────────────────
  { type: "section", key: "section-crescimento", label: "Crescimento" },
  {
    key: "leads",
    label: "Leads",
    icon: ic(TbFilter),
    link: "/business/leads",
  },
  {
    // Promovido a top-level (antes era child do grupo "agenda").
    // Visível apenas para tenants de barbearia (filtro no buildMenu).
    // `requiresScheduling`: fidelidade acumula por atendimento REALIZADO, então
    // as rotas de escrita do programa são gateadas por `requireScheduling` no
    // backend e a intent `query_loyalty` é suprimida no tier (ADR-0026).
    key: "loyalty",
    label: "Fidelidade",
    icon: ic(TbGift),
    link: "/fidelidade",
    requiresSegment: "barbearias",
    requiresScheduling: true,
  },
  {
    key: "follow-up",
    label: "Follow-up",
    icon: ic(TbRepeat),
    link: "/business/follow-up",
  },
  {
    // onClick → /business/reports (no buildMenu).
    key: "reports",
    label: "Relatórios",
    icon: ic(TbChartBar),
  },

  // ── Conta (rodapé) ─────────────────────────────────────────────────────
  { type: "section", key: "section-conta", label: "Conta" },
  {
    key: "support",
    label: "Suporte",
    icon: ic(TbHeadset),
    link: "/business/support",
  },
  {
    key: "settings",
    label: "Configurações",
    icon: ic(TbSettings),
    link: "/user-settings",
  },
];
